import { useEffect, useReducer, useState, useCallback } from "react";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import LoadingModal from "../LoadingModal";
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import styles from './Survey.module.css';
import ModalSurveyQuestionResults from "../ModalSurveyQuestionResults/ModalSurveyQuestionResults";
import ConstantSumQuestion from "../ConstantSum";
import Matrix from "../Matrix";
import MutipleChoiceQuestion from "../MultipleChoiceQuestion";
import TextQuestion from "../TextQuestion";
import NumericQuestion from "../NumericQuestion";

const reducer = (state, action) => {

    let alertStatus;

    switch (action.type) {
        case ACTION.NEW_DATA:
            return { ...state, title: action.payload.title, description: action.payload.description, questions: [...action.payload.questions], surveys: [...action.payload.surveys], loaded: true, notFound: false };
        case ACTION.NEW_RESPONSES:
            return {
                ...state,
                questions: state.questions.map(question =>
                    question.surveyQuestionId === action.payload.surveyQuestionId
                        ? { ...question, responses: [...action.payload.responses] }
                        : question
                )
            };
        case ACTION.LOADING:
            return { ...state, loaded: false, notFound: false };
        case ACTION.HIDE_ALERT:
            alertStatus = { ...state.alertStatus, open: false };
            return { ...state, alertStatus };
        case ACTION.SHOW_ALERT:
            alertStatus = { ...state.alertStatus, severity: action.payload.severity, message: action.payload.message, open: true };
            return { ...state, alertStatus };
        case ACTION.NOT_FOUND:
            return { ...state, loaded: true, notFound: true };
        default:
            console.log(`unhandled action.type: ${action.type}`)
    }
}

const ACTION = {
    NEW_DATA: "newData",
    NEW_RESPONSES: "newResponses",
    LOADING: "loading",
    NOT_FOUND: "notFound",
    SHOW_ALERT: "showAlert",
    HIDE_ALERT: "hideAlert"
}

const Survey = () => {

    const axiosPrivate = useAxiosPrivate();

    const navigate = useNavigate();
    const location = useLocation();

    let { surveyId } = useParams();

    const isInsightsScreen = location.pathname.includes('insights');

    const [state, dispatch] = useReducer(reducer, {
        title: null,
        description: null,
        questions: [],
        surveys: [],
        loaded: false,
        notFound: false,
        alertStatus: {
            open: false
        }
    });

    const [selectedSurveyQuestionId, setSelectedSurveyQuestionId] = useState(null);

    const saveResponse = async (params) => {

        const json = { ...params };

        console.log(json);
        try {
            const response = await axiosPrivate.post(`/surveyQuestions/${params.surveyQuestionId}`,
                JSON.stringify(json),
                {
                    headers: { 'Content-Type': 'application/json' }
                }
            );
            dispatch({
                type: ACTION.NEW_RESPONSES, payload: { ...response.data }
            });
            dispatch({
                type: ACTION.SHOW_ALERT, payload: {
                    severity: "success",
                    message: "Response saved"
                }
            });
        } catch (err) {
            console.log(err);
            if (err.response?.status === 403) {
                navigate('/login', { state: { from: location }, replace: true });
            }
        }
    }

    useEffect(() => {

        let isMounted = true;

        const controller = new AbortController();

        const getData = async () => {

            const url = `/survey/${surveyId}/questions`;

            try {
                const response = await axiosPrivate.get(url,
                    {
                        signal: controller.signal
                    });
                if (isMounted) {
                    dispatch({
                        type: ACTION.NEW_DATA,
                        payload: response.data
                    });
                }
            } catch (err) {
                if (isMounted) {
                    console.error(err);
                    if (err.response?.status === 404) {
                        dispatch({
                            type: ACTION.NOT_FOUND
                        });
                    } else if (err.response?.status === 403) {
                        navigate('/login', { state: { from: location }, replace: true });
                    }
                }
            }
        }

        dispatch({
            type: ACTION.LOADING
        });

        getData();

        return () => {
            isMounted = false;
            controller.abort();
        }

    }, [surveyId]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        dispatch({
            type: ACTION.HIDE_ALERT
        });
    };

    const closeModalSurveyQuestionResultsDialog = useCallback(() => {
        setSelectedSurveyQuestionId(null);
    }, []);

    const showModalSurveyQuestionResultsDialog = useCallback((surveyQuestionId) => {
        console.log(">> showModalSurveyQuestionResultsDialog: " + surveyQuestionId);
        setSelectedSurveyQuestionId(surveyQuestionId);
    }, []);


    if (!state.loaded) {
        return (
            <LoadingModal />
        )
    } else if (state.notFound) {
        return (
            <div className={styles.notFound}>
                <h3>Our apologies, but this survey is not available.</h3>
                <div className={styles.floatingMenu}>
                    <Link className={styles.back} to={(isInsightsScreen) ? "/insights" : "/benchmarking"}>Back to {(isInsightsScreen) ? "Insights" : "Benchmarking"}</Link>
                </div>
            </div>
        )
    } else {
        return (
            <section className={styles.container}>
                <h1 className={styles.title}>{state.title}</h1>
                {
                    state.description && <h2 className={styles.description}>{state.description}</h2>
                }
                {
                    state.questions.map((question, i) => {
                        let elem;
                        switch (question.surveyQuestionType) {
                            case "matrix":
                                elem = <Matrix surveyQuestionId={question.surveyQuestionId} statements={question.statements} answerChoices={question.answerChoices} responses={question.responses} saveResponse={saveResponse} compact={true} />;
                                break;
                            case "multiple choice":
                                elem = <MutipleChoiceQuestion question={question} answerChoices={question.answerChoices} responses={question.responses} saveResponse={saveResponse} compact={true} />;
                                break;
                            case "text":
                                elem = <TextQuestion question={question} answerChoices={question.answerChoices} responses={question.responses} saveResponse={saveResponse} compact={true} />;
                                break;
                            case "numeric":
                                elem = <NumericQuestion question={question} answerChoices={question.answerChoices} responses={question.responses} saveResponse={saveResponse} compact={true} />;
                                break;
                            case "constant sum":
                                elem = <ConstantSumQuestion question={question} statements={question.statements} responses={question.responses} saveResponses={saveResponse} compact={true} />;
                                break;
                            default:
                                elem = null;
                        }
                        return (
                            <div key={i} className={`${styles.questionContainer} ${question.useSliders ? styles.sliderBarQuestion : ""}`}>
                                <ol className="title">
                                    <li value={i + 1}>{question.text}</li>
                                </ol>
                                {elem}
                                <button className={styles.viewResults} onClick={e => showModalSurveyQuestionResultsDialog(question.surveyQuestionId)}>View results</button>
                            </div>
                        )
                    })
                }
                <div className={styles.floatingMenu}>
                    <Link className={styles.back} to={(isInsightsScreen) ? "/insights" : "/benchmarking"}>Back to {(isInsightsScreen) ? "Insights" : "Benchmarking"}</Link>
                    <div className={styles.separator}></div>
                    <Link className={styles.next} to={`/insights/${surveyId}/results`} state={{ fromSurveyId: surveyId }}>Full Survey Results</Link>
                </div>
                <Snackbar open={state.alertStatus.open} autoHideDuration={3000} onClose={handleClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                    <Alert onClose={handleClose} variant="filled" severity={state.alertStatus.severity} sx={{ width: '100%' }}>{state.alertStatus.message}</Alert>
                </Snackbar>
                {selectedSurveyQuestionId && <ModalSurveyQuestionResults surveyQuestionId={selectedSurveyQuestionId} onCloseDialog={closeModalSurveyQuestionResultsDialog} />}
            </section>
        )
    }

};

export default Survey;